/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { HallowABFlag, useAB } from "@packages/ab";
import type { AdminDashUser } from "@packages/sdk";
import { AccountType, RoleID } from "@packages/sdk";
import type {
  ConfirmShowProps,
  HallowElementProps,
  ThemeType,
} from "@packages/ui";
import {
  Avatar,
  Form,
  FormField,
  Loader,
  SelectMenu,
  Skeleton,
  spacing,
  standardSchemaResolver,
  TextArea,
  TextInput,
  useAlert,
  useForm,
} from "@packages/ui";
import { useEffect, useMemo, useRef } from "react";
import type { FieldErrors } from "react-hook-form";
import type { z } from "zod";

import { usernameErrorMap } from "../../../lib/utils/usernameErrorMap";
import { useRequestGenerateStudentUsername } from "../../../requests/useRequestGenerateStudentUsername";
import { SubscriptionText } from "../SubscriptionText";
import { AdminDashSpecialSelect } from "./MembersList/AdminDashSpecialSelect";
import { WarningNotice } from "./WarningNotice";

const formStyles: Interpolation = {
  width: spacing.full,
};

const contentStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  justifyContent: "flex-start",
  width: spacing.full,
};

const inputStyles: Interpolation = {
  "&:disabled": { cursor: "default !important" },
  width: spacing.full,
};

const inputSkeletonStyles: Interpolation = {
  height: spacing.xxxl,
};

const ulStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.errorError,
  listStyle: "disc",
  marginLeft: spacing.m,
});
type SingleMemberFormProps = Omit<HallowElementProps<"form">, "onSubmit"> & {
  user?: Partial<AdminDashUser> & { subscription_end_date?: string };
  image_url?: AdminDashUser["image_url"];
  readOnly?: boolean;
  loading?: boolean;
  onBeforeSubmit?: (values: Partial<AdminDashUser>) => void;
  onSubmit?: (values: Partial<AdminDashUser>) => void | Promise<void>;
  schema: z.ZodObject;
  validationSchema?: z.ZodSchema;
  hideRoleSelect?: boolean;
};
type AdminDashUserForm = AdminDashUser & {
  login_method?: "email" | "username";
};
export const SingleMemberForm = ({
  user,
  image_url,
  schema,
  validationSchema = schema,
  onBeforeSubmit,
  onSubmit,
  loading,
  readOnly = false,
  children,
  hideRoleSelect,
  ...props
}: SingleMemberFormProps) => {
  const t = useTranslations();
  const { flags } = useAB();
  const alert = useAlert<ConfirmShowProps & { errors: FieldErrors }>({
    description: ({ errors }) => (
      <ul css={ulStyles}>
        {Object.entries(errors).map(([key, err]) => (
          <li key={key}>
            {placeholders[key]}:{" "}
            {typeof err.message === "string"
              ? usernameErrorMap(t, err.message)
              : err.message}
          </li>
        ))}
      </ul>
    ),
  });
  const dataRef = useRef<Partial<AdminDashUser>>(user);

  /*
   * this is because HTML forms > Radix-UI primitives technically use strings only, even for non-strings,
   * but react-hook-forms uses exact typing, so we need the form schema to match the HTML form or else
   * nothing works, but we want the actual schema to match the enum for code clarity and consistency with the backend
   */
  const formUser = useMemo(() => {
    const formUser: any = {};
    for (const prop in user) {
      if (user.hasOwnProperty(prop)) {
        formUser[prop] = user[prop]?.toString();
      }
    }
    // don't have undefined values; React hates switching from uncontrolled to controlled fields
    for (const prop of Object.keys(schema.shape)) {
      if (!formUser[prop]) formUser[prop] = "";
    }
    return formUser;
  }, [user]);

  const form = useForm({
    mode: "onSubmit",
    values: formUser,
    resolver: standardSchemaResolver(validationSchema),
    disabled: readOnly,
  });

  useEffect(() => {
    form.watch((values) => (dataRef.current = values));
  }, [form.watch]);

  const chosenLoginMethod = form.watch("login_method");
  const chosenAccountType = form.watch("account_type");
  const chosenRole = form.watch("role_id");
  const enteredName = form.watch("name");
  const enteredLastName = form.watch("last_name");

  const nameForGeneration = enteredName ?? user?.name ?? "";
  const lastNameForGeneration = enteredLastName ?? user?.last_name ?? "";
  const usernameQuery = useRequestGenerateStudentUsername({
    name: nameForGeneration,
    last_name: lastNameForGeneration,
    enabled:
      chosenLoginMethod === "username" &&
      nameForGeneration.length > 0 &&
      lastNameForGeneration.length > 0,
  });

  const generatedUsername = useRef<string | null>(null);

  useEffect(() => {
    const currentUsername = form.getValues("username");
    if (usernameQuery.data?.username?.length) {
      if (
        !currentUsername?.length ||
        currentUsername === generatedUsername.current
      ) {
        form.setValue("username", usernameQuery.data.username);
      }
      generatedUsername.current = usernameQuery.data.username;
    }
  }, [usernameQuery.data?.username]);

  const visibleRole = useMemo(() => {
    if (readOnly) return user?.role_id;
    return chosenRole ?? user?.role_id;
  }, [chosenRole, user?.role_id, readOnly]);
  const visibleLoginMethod = useMemo(() => {
    if (!flags[HallowABFlag.WebAdminDashKidsProfile]?.isTargeted)
      return "email";
    if (readOnly) return !!user?.email?.length ? "email" : "username";
    return chosenLoginMethod ?? (!!user?.email?.length ? "email" : "username");
  }, [readOnly, user?.email, chosenLoginMethod, flags]);
  const visibleAccountType = useMemo(() => {
    if (readOnly) return user?.account_type;
    return chosenAccountType ?? user?.account_type;
  }, [chosenAccountType, user?.account_type, readOnly]);

  const placeholders: { [key in keyof AdminDashUserForm]?: string } = {
    bio: t("profile_bio"),
    email: t("admin_dash_email_required"),
    last_name: t("admin_dash_last_name_required"),
    name: t("admin_dash_first_name_required"),
    role_id: t("admin_dash_role_required"),
    username: t("profile_username"),
    account_type: t("web_admin_dash_account_type_required"),
    login_method: t("web_admin_dash_login_method_required"),
    parent_email: t("coppa_registration_parent_email_placeholder"),
  };

  if (!formUser) {
    return <Loader />;
  }

  return (
    <Form
      {...form}
      formCSS={formStyles}
      formProps={{
        onSubmit: (e) => {
          onBeforeSubmit(dataRef.current);
          form.handleSubmit(onSubmit, async (errors) => {
            await alert.show({
              title: t("general_word_error"),
              errors,
            });
          })(e);
        },
        ...props,
      }}
    >
      <div css={contentStyles}>
        {readOnly && (
          <Avatar
            imgSrc={image_url}
            name={user?.name}
            size={116}
            loading={loading}
          />
        )}
        {user?.subscription_end_date && (
          <SubscriptionText
            isShowDate
            subscription_end_date={user.subscription_end_date as string}
          />
        )}
        {Object.keys(schema.shape).map((key) => {
          if (loading) {
            return <Skeleton key={key} css={inputSkeletonStyles} />;
          }
          if (key === "bio") {
            return (
              <TextArea.Field
                key={key}
                name={key}
                control={form.control}
                placeholder={placeholders.bio}
                css={inputStyles}
              />
            );
          }
          if (key === "role_id") {
            if (hideRoleSelect)
              return (
                <FormField
                  render={({ field }) => <input type="hidden" {...field} />}
                  name={key}
                  key={key}
                  control={form.control}
                  css={{ display: "none" }}
                />
              );
            return (
              <SelectMenu.Field
                key={key}
                name={key}
                control={form.control}
                placeholder={placeholders.role_id}
                disabled={readOnly}
                options={[
                  {
                    value: RoleID.Student.toString(),
                    label: t("admin_dash_role_student"),
                  },
                  {
                    value: RoleID.Teacher.toString(),
                    label: t("admin_dash_role_teacher"),
                  },
                ]}
              />
            );
          }
          if (key === "login_method") {
            return (
              <AdminDashSpecialSelect.Field
                key={key}
                name={key}
                control={form.control}
                placeholder={placeholders.login_method}
                disabled={readOnly}
                options={[
                  {
                    value: "email",
                    label: t("web_admin_dash_login_method_email"),
                    explanation: t(
                      "web_admin_dash_login_method_email_description",
                    ),
                  },
                  {
                    value: "username",
                    label: t("web_admin_dash_login_method_username"),
                    explanation: t(
                      "web_admin_dash_login_method_username_description",
                    ),
                  },
                ]}
              />
            );
          }
          if (key === "account_type") {
            return (
              <AdminDashSpecialSelect.Field
                key={key}
                name={key}
                control={form.control}
                placeholder={placeholders.account_type}
                disabled={readOnly}
                options={[
                  {
                    value: AccountType.Kid,
                    label: t("web_admin_dash_account_type_kids"),
                    explanation: t(
                      "web_admin_dash_account_type_kids_description",
                    ),
                  },
                  {
                    value: AccountType.Standard,
                    label: t("web_admin_dash_account_type_standard"),
                    explanation: t(
                      "web_admin_dash_account_type_standard_description",
                    ),
                  },
                ]}
              />
            );
          }

          if (
            key === "email" &&
            parseInt(visibleRole?.toString()) === RoleID.Student &&
            visibleLoginMethod !== "email"
          )
            return null;
          if (key === "parent_email" && visibleAccountType !== AccountType.Kid)
            return null;
          if (key === "username" && visibleLoginMethod !== "username")
            return null;
          const placeholder = placeholders[key];
          if (!placeholder) return null;
          return (
            <TextInput.Field
              key={key}
              name={key}
              control={form.control}
              type={
                key === "email" || key === "parent_email" ? "email" : "text"
              }
              placeholder={placeholder}
              css={inputStyles}
            />
          );
        })}
      </div>
      {!!user?.account_type && user.account_type !== chosenAccountType ? (
        <WarningNotice
          message={
            chosenAccountType === AccountType.Kid
              ? "web_admin_dash_account_type_kids_warning"
              : "web_admin_dash_account_type_standard_warning"
          }
        />
      ) : null}
      {children}
    </Form>
  );
};
